'use client';

import { getVisibleContentFields } from '@eliadmin/core/src/utils/content-fields';
import { contentFieldKey } from '@eliadmin/core/src/api/content-fields/public-content-fields';
import { ContentFieldBlock } from '@eliadmin/core/src/components/content-fields/content-fields';
import { useEffect, useMemo, useState } from 'react';

import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ProductTabsDescription } from './product-tabs-description';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { ProductTabsDocuments } from './product-tabs-documents';
import { ProductTabsReviews } from '../product-tabs-reviews';
import { ProductAttributes } from './product-attributes';

function getGridColsClass(count: number) {
  const cols = Math.min(Math.max(count, 1), 12);
  return `grid-cols-${cols}`;
}

type TabDef = {
  id: string;
  title: string;
  content: React.ReactNode;
  show: boolean;
};

export function ProductTabs({ product, locale }: { product: ProductDetailData; locale: string }) {
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const desiredObject = product.variants?.length ? selectedVariant : product;

  const { t } = useTranslations();

  const tabs = useMemo<TabDef[]>(
    () => [
      {
        id: 'description',
        title: t('text274'),
        content: <ProductTabsDescription product={product} selectedVariant={selectedVariant} />,
        show: !!desiredObject?.description,
      },
      {
        id: 'documents',
        title: t('text2406'),
        content: <ProductTabsDocuments product={product} />,
        show: !!(product?.attachments && product.attachments.length > 0),
      },
      {
        id: 'attributes',
        title: t('text9'),
        content: <ProductAttributes product={product} />,
        show: !!(product?.attributes && product.attributes.length > 0),
      },
      {
        id: 'reviews',
        title: t('text447'),
        content: <ProductTabsReviews product={product} locale={locale} />,
        show: true,
      },

      ...getVisibleContentFields(product).map(field => ({
        id: contentFieldKey('products', field.id),
        key: contentFieldKey('products', field.id),
        name: field.name,
        title: field.name,
        content: <ContentFieldBlock field={field} module="products" />,
        show: true,
      })),
    ],
    [t, product, selectedVariant, desiredObject?.description, locale],
  );

  const visibleTabs = useMemo(() => tabs.filter(tab => tab.show), [tabs]);

  const [activeTab, setActiveTab] = useState<TabDef['id']>('description');

  // Ensure the first available tab is selected on initial load and whenever visible tabs change.
  useEffect(() => {
    const firstId = visibleTabs[0]?.id;
    if (!firstId) return;

    const isActiveStillVisible = visibleTabs.some(t => t.id === activeTab);
    if (!isActiveStillVisible) setActiveTab(firstId);
  }, [visibleTabs, activeTab]);

  if (visibleTabs.length === 0) return null;

  return (
    <section className={cn('custom-section', 'pb-24 pt-12')}>
      <Tabs
        value={activeTab}
        onValueChange={v => setActiveTab(v as TabDef['id'])}
        className={cn(
          'custom-container',
          'flex flex-col items-center justify-center gap-6 lg:gap-8',
        )}
      >
        <TabsList
          className={cn(
            'hidden h-fit w-full gap-0 rounded-[4px] border border-[#EDEDED] bg-transparent bg-white p-1 shadow-none sm:grid',
            getGridColsClass(visibleTabs.length),
          )}
        >
          {visibleTabs.map(tab => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className={cn(
                'col-span-1 h-12 w-full rounded-[4px] text-sm font-semibold uppercase shadow-none transition-all',
                'ea-product-tabs__tabs-trigger',
              )}
            >
              {tab.title}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsList className="hide-scroll-bar flex h-fit w-full items-center justify-start gap-4 overflow-x-scroll rounded-none bg-transparent p-0 sm:hidden">
          {visibleTabs.map(tab => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className={cn(
                'whitespace-nowrap rounded-none p-0 text-sm font-semibold uppercase',
                'ea-product-tabs__tabs-trigger-mobile',
              )}
            >
              {tab.title}
            </TabsTrigger>
          ))}
        </TabsList>

        {visibleTabs.map(tab => (
          <TabsContent key={tab.id} className="w-full" value={tab.id}>
            {tab.content}
          </TabsContent>
        ))}
      </Tabs>
    </section>
  );
}
